import axios, { AxiosRequestConfig, InternalAxiosRequestConfig, Method } from 'axios';
import CryptoJS from 'crypto-js';
// 导入自动生成的接口类型文件
import { ApiParamsDefinition, ApiResponseDefinition } from '@/api/types/api-definition';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getBrowserEnvironment } from '@/utils/tools';
// import { cookies, headers } from 'next/headers';
import { toast } from '@/components/Toast';
import { ProviderType, ReportingCenter } from '@/reporting';
import { SensorsProvider } from '@/reporting/providers/sensorProvider';
import {
  createIdentityRequestSnapshot,
  readIdentityStorage,
  recordIdentityResponse,
} from '@/reporting/identityDiagnostics';
import { getStoreInstance } from '@/store';
import { reportApiFailure } from '@/services/sentry';
import { env } from '@/utils/env';
import { performLogout } from '@/utils/logout';
import { Base64 } from 'js-base64';
import logger from './client-logger';
import { SIGNATURE_API_LIST } from './constants';
import { serializeAxiosErrorForClientLog } from './axios-error-log';

// 本地开发环境：zeelool-next是因为加了basePath来做新老框架的内嵌路径区分，backend是来表示请求走向的是PHP，
// 而不是在Nextjs这层处理
const instance = axios.create({
  baseURL:
    env.NEXT_PUBLIC_ENV === 'local' ? '/backend' : env.NEXT_PUBLIC_ZEELOOL_SERVER_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

export type HttpRequestTransportOptions = Exclude<
  AxiosRequestConfig<unknown>,
  'data' | 'params'
> & {
  /**
   * Guest-only endpoints must never receive a stale Authorization header.
   * This flag is consumed by the interceptor and is not sent to the server.
   */
  skipAuth?: boolean;
};

type IdentityDiagnosticConfig = InternalAxiosRequestConfig & {
  identityDiagnosticSnapshot?: ReturnType<typeof createIdentityRequestSnapshot>;
};

function getResponseTraceId(headers: unknown): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  const traceHeaders = headers as Record<string, unknown> & {
    get?: (header: string) => unknown;
  };
  const candidates = ['trace-id', 'x-trace-id', 'trace_id', 'x-request-id'];
  for (const header of candidates) {
    const value = traceHeaders.get?.(header) ?? traceHeaders[header];
    if (typeof value === 'string' && value) return value.slice(0, 128);
  }
  return undefined;
}

// 请求拦截器
/**
 * 为请求补充身份、设备与风控信息，并为指定认证接口生成签名及安全的双渠道诊断事件。
 * @param config Axios 原始请求配置；验签接口需提供对象形式的 data。
 * @returns 完成请求头和签名处理后的 Axios 请求配置。
 */
// 请求拦截器
instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config };
  const skipAuth = Boolean(
    (config as InternalAxiosRequestConfig & { skipAuth?: boolean }).skipAuth,
  );
  delete (newConfig as InternalAxiosRequestConfig & { skipAuth?: boolean }).skipAuth;
  // 神策 AB 测相关代码
  const sessionDistinctId =
    sessionStorage.getItem('zl-distinct_id') &&
    sessionStorage.getItem('zl-distinct_id') !== 'undefined'
      ? sessionStorage.getItem('zl-distinct_id')
      : '';
  const sessionAnonymousId =
    sessionStorage.getItem('zl-anonymous_id') &&
    sessionStorage.getItem('zl-anonymous_id') !== 'undefined'
      ? sessionStorage.getItem('zl-anonymous_id')
      : '';

  let distinctId = sessionDistinctId;
  const center = ReportingCenter.getInstance();
  const sensorsProvider = center.getProvider(ProviderType.SENSORS) as SensorsProvider;
  // 因为性能优化，神策脚本的延后处理，需要等待神策脚本加载完成才能获取到distinctId
  if (!distinctId) {
    if (sensorsProvider) {
      if (sensorsProvider.isInitialized()) {
        distinctId = window.sensorsDataAnalytic201505?.store?.getDistinctId();
      } else {
        await new Promise((resolve) => {
          const intv = setInterval(() => {
            if (sensorsProvider.isInitialized()) {
              clearInterval(intv);
              resolve('init');
            }
          }, 100);
        });
        distinctId = window.sensorsDataAnalytic201505?.store?.getDistinctId();
      }
    }
  }
  const anonymousId =
    sessionAnonymousId || window.sensorsDataAnalytic201505?.quick('getAnonymousID'); // 获取神策的匿名 ID

  newConfig.headers['distinct-id'] = distinctId;
  newConfig.headers['anonymous-id'] = anonymousId;

  document.cookie = `distinct-id=${distinctId}; path=/; max-age=31536000; samesite=lax`;
  document.cookie = `anonymous-id=${anonymousId}; path=/; max-age=31536000; samesite=lax`;

  // 身份信息追加
  const token =
    env.NEXT_PUBLIC_PARENT_IFRAME_TOKEN || readIdentityStorage('zl-token', 'axios_interceptor');
  if (skipAuth) {
    newConfig.headers.delete('Authorization');
  } else if (token) {
    newConfig.headers.Authorization = `${token} `;
  }
  // 游客处理
  const visitorId = readIdentityStorage('zl-visitor_id', 'axios_interceptor');
  if (visitorId) {
    newConfig.headers['visitor-id'] = visitorId;
  }
  if (config.url?.includes('/api/cart/store')) {
    (newConfig as IdentityDiagnosticConfig).identityDiagnosticSnapshot =
      createIdentityRequestSnapshot({
        endpoint: config.url,
        tokenStorage: token ? 'present' : 'missing',
        visitorStorage: visitorId ? 'present' : 'missing',
        authorizationPrepared: !skipAuth && Boolean(token),
        visitorHeaderPrepared: Boolean(visitorId),
      });
  }
  // store_id 设备端标识：1 pc;4 wap（按 UA 判断）
  if (!config.headers['store-id']) {
    let userAgent: string | null = '';
    userAgent = window.navigator.userAgent;
    newConfig.headers['store-id'] = EPlatform[getBrowserEnvironment(userAgent || '')];
  }

  // 设置 AWS WAF token

  try {
    const wafToken = await window.AwsWafIntegration?.getToken();
    if (wafToken) {
      newConfig.headers['x-aws-waf-token'] = wafToken;
      logger.log(`addAwsWafToken success: ${wafToken}`);
    }
  } catch (error) {
    logger.error(`addAwsWafToken error: ${error}`);
  }

  // 如果该接口需要验签，那么追加signature参数
  if (SIGNATURE_API_LIST.includes(config.url)) {
    newConfig.data.timestamp = parseInt((Date.now() / 1000).toString(), 10);
    const secretKey = Base64.decode(env.NEXT_PUBLIC_ENCRYPT_CODE);
    const request_params = JSON.parse(JSON.stringify(config.data)) as any;
    // 与后端达成一致，将对象的键按字母顺序排列
    const sortedKeys = Object.keys(config.data).sort();
    // 将上报给神策的密码加盐MD5
    if (sortedKeys.includes('password') && request_params.password) {
      request_params.password = CryptoJS.MD5(request_params.password + secretKey).toString(
        CryptoJS.enc.Hex,
      );
    }
    // 需要把参数拼接成 k1v1k2v2secret的格式
    let sortedParams = '';
    // 上报给神策的参数
    let sorted_params_string = '';
    sortedKeys.forEach((key) => {
      // 接口请求的时候，会自动过滤掉值为undefined的参数
      if (config.data[key] !== undefined) {
        // 与后端达成一致，非字符串类型都转成json字符串
        sortedParams += `${key}${typeof config.data[key] !== 'string' ? JSON.stringify(config.data[key]) : config.data[key]}`;
        if (key === 'password') {
          sorted_params_string += `${key}${typeof request_params[key] !== 'string' ? JSON.stringify(request_params[key]) : request_params[key]}`;
        } else {
          sorted_params_string += `${key}${typeof config.data[key] !== 'string' ? JSON.stringify(config.data[key]) : config.data[key]}`;
        }
      }
    });
    sortedParams += `secret${secretKey}`;
    sorted_params_string += `secret${secretKey}`;
    // console.log(sortedParams)
    // 将拼接好的参数进行HmacSHA256加密
    const hash = CryptoJS.HmacSHA256(sortedParams, secretKey);
    // console.log(hash)
    // 转成16进制字符串
    const hexHash = hash.toString(CryptoJS.enc.Hex);
    // console.log(hexHash)
    // base64再加密
    const base64Hash = Base64.encode(hexHash);
    // console.log(base64Hash)
    // 需要加密的接口需要新增签名字段
    config.data.signature = base64Hash;

    // 上报神策
    if (sensorsProvider) {
      sensorsProvider.track({
        eventName: 'ApiCheckSignatureLog',
        properties: {
          request_params,
          sorted_params_string: Base64.encode(sorted_params_string),
          signature: config.data.signature,
        },
      });
    }
  }

  return newConfig;
});
// 响应拦截器
instance.interceptors.response.use(
  (response) => {
    // 判断响应是否是 Blob
    if (response?.data instanceof Blob) {
      return response;
    }
    recordIdentityResponse(
      (response.config as IdentityDiagnosticConfig).identityDiagnosticSnapshot,
      {
        businessCode: Number.isFinite(Number(response?.data?.code))
          ? Number(response?.data?.code)
          : undefined,
        httpStatus: response.status,
        traceId: getResponseTraceId(response.headers),
      },
    );
    // logger.log(`response.data: ${JSON.stringify(response)}`);
    if (response?.data?.code === 401) {
      toast.error(response.data.msg);

      performLogout({
        shouldCallLogouApi: false,
      });

      // 【SWR 规范对齐】必须 reject 错误，防止 SWR 缓存静默失效或把 undefined 误认为合法数据
      return Promise.reject(new Error(response.data.msg || 'Unauthorized'));
    }

    if (Number(response?.data?.code) >= 500) {
      reportApiFailure(response.data, {
        businessCode: Number(response.data.code),
        isOnline: typeof navigator === 'undefined' ? undefined : navigator.onLine,
        method: response.config?.method,
        page: typeof window === 'undefined' ? undefined : window.location.pathname,
        renderMode: 'csr',
        requestUrl: response.config?.url,
        responseMessage: response.data?.message || response.data?.msg,
        status: response.status,
      });
    }

    return response.data;
  },
  (error) => {
    const code = parseInt(error.response && error.response.status, 10);
    recordIdentityResponse(
      (error.config as IdentityDiagnosticConfig | undefined)?.identityDiagnosticSnapshot,
      {
        httpStatus: Number.isFinite(code) ? code : undefined,
        traceId: getResponseTraceId(error.response?.headers),
        transportError: true,
      },
    );
    reportApiFailure(error, {
      isOnline: typeof navigator === 'undefined' ? undefined : navigator.onLine,
      page: typeof window === 'undefined' ? undefined : window.location.pathname,
      renderMode: 'csr',
    });
    logger.error(`axios error: ${serializeAxiosErrorForClientLog(error)}`);
    if (code === 405 && error.response.headers['x-amzn-waf-action'] === 'captcha') {
      // 打开 AWS Captcha 对话框
      const store = getStoreInstance();
      logger.log(`getStoreInstance result: ${store ? 'store exists' : 'store is null'}`);
      if (store) {
        logger.log(
          'Before setAwsCaptchaDialogShow, current state:',
          store.getState().awsCaptchaDialog,
        );
        store.getState().setAwsCaptchaDialogShow(true);
        logger.log(
          'After setAwsCaptchaDialogShow, current state:',
          store.getState().awsCaptchaDialog,
        );
      } else {
        logger.error('Store instance is null, cannot show AWS Captcha dialog');
      }
    } else if (code === 404) {
      window.location.href = '/404';
    } else if (code === 401) {
      performLogout({
        shouldCallLogouApi: false,
      });
    }

    // 【SWR 规范对齐】底层异常必须继续向外抛出
    return Promise.reject(error);
  },
);

// 后续把这套逻辑整合到@zeelool/toolkit
/**
 * @param url - 链接
 * @param params - 要传给后台的参数：直接键值对为整合进链接中的值，params 和 data 同 axios
 * @param axiosParams - axios 参数，除 params 和 data 外。
 * @returns 返回一个 Promise 对象
 */
export function httpRequest<URL extends keyof ApiParamsDefinition>(
  url: URL,
  params?: ApiParamsDefinition[URL],
  toastConfig = { success: false, fail: true },
  axiosParams?: HttpRequestTransportOptions,
): Promise<ApiResponseDefinition[URL]> {
  return new Promise((resolve, reject) => {
    const requestParams: AxiosRequestConfig<unknown> = axiosParams ? { ...axiosParams } : {};
    try {
      // 处理请求参数
      requestParams.data = (params as any)?.data;
      requestParams.params = (params as any)?.params;
      requestParams.method = url.split(':')[0] as Method;
      requestParams.url = url.split(':')[1];

      /**
       * 替换动态参数的链接，比如
       * 如果 URL 是 GET:/users/{id}/posts/{postId}，而传入的 params 是 { id: 123, postId: 456 }
       * 那么这段代码会将 URL 转换为 /users/123/posts/456
       */
      if (requestParams.url?.match(/\{(.+?)\}/g)?.length) {
        requestParams.url.match(/\{(.+?)\}/g)?.forEach((item) => {
          if (item) {
            const key = item.substring(1, item?.length - 1);
            requestParams.url = requestParams.url?.replace(
              `/${item}`,
              (params as any)[key] !== null && (params as any)[key] !== undefined
                ? `/${(params as any)[key]}`
                : '',
            );
          }
        });
      }
    } catch (err) {
      reject(err);
    }

    // 因为现有后端接口的返回数据各种杂乱无章，不按套路来，有几种场景
    // 接口请求成功，需要toast提示，提示的消息来源于后端给的msg字段
    // 接口请求成功，不需要toast提示
    // 接口请求失败，需要toast提示，提示的消息也是来源于后端给的msg字段
    // 接口请求失败，不需要toast提示
    instance(requestParams)
      .then((res) => {
        if (toastConfig && toastConfig.success && (res as any)?.code === 200) {
          toast.success((res as any).msg);
        } else if (toastConfig && toastConfig.fail && (res as any)?.code > 400) {
          toast.error((res as any).msg);
        }

        resolve(res as any);
      })
      .catch((err) => {
        reject(err);
      });
  });
}
